import React, {FC, useMemo, useState} from 'react';
import {connect} from 'react-redux';

import {makeMapStateToProps, ResolveStateProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {OnboardingEventsEnum, trackEvent, TrackingTypesEnum} from '../../../services/tracking';
import {completeCompanyOnboardingAction} from '../../../store/data/company/companyActions';
import {updateSelectedSideFrameIdAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {saveWasOnboardingCompletedAction} from '../../../store/data/preferences/preferencesActions';
import {AppPanelId} from '../../../store/panels/panelStateBase';
import {
  getHasFirstGlobalAdminWelcomeCarousel,
  getHasTeammateWelcomeCarousel,
} from '../../../store/stateless/welcomeCarouselSelectors';
import {Carousel} from '../carousel';
import {makeWelcomeCarouselCompanySlide} from './welcomeCarouselCompanySlide';
import {welcomeCarouselPersonaSlide} from './welcomeCarouselPersonaSlide';

/*
 * Props.
 */

interface WelcomeCarouselContainerProps {
  panelId: AppPanelId;
}

/*
 * Component.
 */

type WelcomeCarouselContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type WelcomeCarouselContainerDispatchProps = ResolveDispatchProps<typeof dispatchProps>;
type WelcomeCarouselContainerComponentProps = WelcomeCarouselContainerProps &
  WelcomeCarouselContainerStateProps &
  WelcomeCarouselContainerDispatchProps;

const WelcomeCarouselContainerComponent: FC<WelcomeCarouselContainerComponentProps> = (props) => {
  const {
    panelId,
    hasFirstGlobalAdminWelcomeCarousel,
    hasTeammateWelcomeCarousel,
    saveWasOnboardingCompleted,
    completeCompanyOnboarding,
    updateSelectedSideFrameId,
  } = props;

  const welcomeCarouselCompanySlide = useMemo(() => makeWelcomeCarouselCompanySlide({panelId}), [panelId]);
  const slides = hasFirstGlobalAdminWelcomeCarousel
    ? [welcomeCarouselPersonaSlide, welcomeCarouselCompanySlide]
    : [welcomeCarouselPersonaSlide]; // Show only the persona slide for invited teammates.

  const [isCarouselOpen, setIsCarouselOpen] = useState(
    hasFirstGlobalAdminWelcomeCarousel || hasTeammateWelcomeCarousel,
  );

  // On completion, save that the onboarding was completed, and close the carousel.
  const onRequestClose = () => {
    saveWasOnboardingCompleted();

    // Completing the company onboarding is only relevant for first global admins.
    if (hasFirstGlobalAdminWelcomeCarousel) {
      completeCompanyOnboarding();
    } else {
      // Immediately open the start guide and track completion events for users who are not the first global admin.
      updateSelectedSideFrameId(FrameTypesEnum.START_GUIDE);
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED_PERSONNA);
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED);
    }

    setIsCarouselOpen(false);
  };

  if (!isCarouselOpen) {
    return null;
  }

  return <Carousel slides={slides} onRequestClose={onRequestClose} />;
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  hasFirstGlobalAdminWelcomeCarousel: getHasFirstGlobalAdminWelcomeCarousel,
  hasTeammateWelcomeCarousel: getHasTeammateWelcomeCarousel,
}));

const dispatchProps = {
  saveWasOnboardingCompleted: saveWasOnboardingCompletedAction,
  completeCompanyOnboarding: completeCompanyOnboardingAction,
  updateSelectedSideFrameId: updateSelectedSideFrameIdAction,
};

export const WelcomeCarouselContainer = connect(
  mapStateToProps,
  dispatchProps,
)(WelcomeCarouselContainerComponent);
